// LEGACY — superseded by features/client-rental-config/. Scheduled for deletion in 2 sprints.
import { createSlice } from "@reduxjs/toolkit";
import {
  getClientRentalConfigs,
  getClientRentalConfigByClientId,
  updateClientRentalConfig,
} from "./thunk";

export interface ClientRentalConfig {
  id: number;
  clientId: number;
  clientName: string;
  overrideGracePeriodDays: number | null;
  overrideMinimumHireWeeks: number | null;
  overrideInvoiceFrequency: number | null;
  overrideInvoiceDay: number | null;
  overrideIncludeWeekends: boolean;
  overrideExcludePublicHolidays: boolean;
  overrideNotifyOnOverdueRentals?: boolean;
  overrideOffHireReminderDays?: number | null;
  overrideNotifyOnDraftInvoiceGeneration?: boolean;
  reason: string;
  effectiveFrom: string;
  effectiveTo: string;
  approvedByUserId: string;
  approvedDate: string;
  tenantId?: number;
  updatedAt?: string;
}

export interface ClientRentalConfigState {
  configs: ClientRentalConfig[];
  config: ClientRentalConfig | null;
  error: any;
  loading: boolean;
  isConfigSuccess: boolean;
}

export const initialState: ClientRentalConfigState = {
  configs: [],
  config: null,
  error: null, // Changed from {} to null to avoid React rendering error
  loading: false,
  isConfigSuccess: false,
};

const ClientRentalConfigSlice = createSlice({
  name: "ClientRentalConfigSlice",
  initialState,
  reducers: {
    resetClientRentalConfig: (state) => {
      console.log("Reducer: resetClientRentalConfig - clearing config and error");
      state.config = null;
      state.error = null; // Changed from {} to null
      state.loading = false;
      state.isConfigSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getClientRentalConfigs.pending,
      (state: any) => {
        state.loading = true;
        state.error = null; // Changed from {} to null
      }
    );
    builder.addCase(
      getClientRentalConfigs.fulfilled,
      (state: any, action: any) => {
        state.configs = action.payload;
        state.loading = false;
        state.isConfigSuccess = true;
      }
    );
    builder.addCase(
      getClientRentalConfigs.rejected,
      (state: any, action: any) => {
        state.loading = false;
        state.error = action.payload?.error || action.error || null;
        state.isConfigSuccess = false;
      }
    );

    builder.addCase(
      getClientRentalConfigByClientId.pending,
      (state: any) => {
        state.loading = true;
        state.error = null; // Changed from {} to null
      }
    );
    builder.addCase(
      getClientRentalConfigByClientId.fulfilled,
      (state: any, action: any) => {
        console.log("Reducer: getClientRentalConfigByClientId.fulfilled", {
          payload: action.payload,
          payloadType: typeof action.payload,
          payloadIsNull: action.payload === null,
          payloadIsUndefined: action.payload === undefined,
          payloadKeys: action.payload && typeof action.payload === 'object' && !Array.isArray(action.payload) ? Object.keys(action.payload) : 'not an object',
          payloadClientId: action.payload?.clientId,
          currentStateConfig: state.config
        });
        
        // Store the payload directly - it should be the config object or null
        state.config = action.payload;
        state.loading = false;
        state.isConfigSuccess = true;
        
        // Clear error when we successfully get a response (even if null - "not found" is not an error)
        if (action.payload === null || action.payload === undefined) {
          state.error = null; // Clear error for "not found" case
          console.log("Reducer: Cleared error - no config found (not an error)");
        } else {
          // Only clear error if we got valid config data
          state.error = null;
        }
        
        console.log("Reducer: Updated state.config to:", state.config);
        console.log("Reducer: State after update:", {
          config: state.config,
          loading: state.loading,
          error: state.error,
          isConfigSuccess: state.isConfigSuccess
        });
      }
    );
    builder.addCase(
      getClientRentalConfigByClientId.rejected,
      (state: any, action: any) => {
        state.loading = false;
        // Extract error message as string, not object
        const errorPayload = action.payload;
        const errorMessage = typeof errorPayload === 'string' 
          ? errorPayload 
          : errorPayload?.message || errorPayload?.error || action.error?.message || 'Failed to fetch client rental config';
        state.error = errorMessage;
        state.isConfigSuccess = false;
      }
    );

    builder.addCase(
      updateClientRentalConfig.pending,
      (state: any) => {
        state.loading = true;
        state.error = null; // Changed from {} to null
      }
    );
    builder.addCase(
      updateClientRentalConfig.fulfilled,
      (state: any, action: any) => {
        console.log("Reducer: updateClientRentalConfig.fulfilled", {
          payload: action.payload,
          currentConfig: state.config
        });
        state.loading = false;
        // Check if config already exists in configs array
        const existingIndex = state.configs.findIndex(
          (config: any) => config.clientId === action.payload.clientId
        );
        
        if (existingIndex !== -1) {
          // Update existing config
          state.configs[existingIndex] = action.payload;
        } else {
          // Add new config
          state.configs.push(action.payload);
        }
        
        state.config = action.payload;
        state.isConfigSuccess = true;
        console.log("Reducer: Updated state.config to:", state.config);
      }
    );
    builder.addCase(
      updateClientRentalConfig.rejected,
      (state: any, action: any) => {
        state.loading = false;
        state.error = action.payload?.error || action.error || null;
        state.isConfigSuccess = false;
      }
    );
  },
});

export const { resetClientRentalConfig } = ClientRentalConfigSlice.actions;
export default ClientRentalConfigSlice.reducer;